import { MethodType } from 'components/DropDown/MethodType';
import { ValidationControl } from 'components/ValidationControl/ValidationControl';
import { dbConstIsBusinessDate } from 'constants/DbConstants';
import { fetchData } from 'data/DataConnector';
import { DateBox } from 'devextreme-react';
import moment from 'moment';
import React, { useState, useEffect } from 'react';
import { connect } from 'react-redux';
import uniqid from 'uniqid';
import { localeDateString, parseDate, validateDate } from 'util/helpers/helperFunctions';
import { Theme } from '../GridControl';

export enum DatePickerPosition {
  absolute,
  fixed,
  relative,
  inherit,
  initial,
}

export interface IProps {
  isDisabled?: boolean;
  onBlur?: (date: string, name: string | undefined) => void;
  name?: string;
  value: Date | undefined;
  onChange: any;
  isRequired: boolean;
  isValid?: boolean;
  validationMessage?: string;
  datePickerPosition?: DatePickerPosition;
  checkBusinessDay?: boolean;
  nbdAdjustment?: string;
  jurisdictionCodesString?: string;
  style?: any;
  theme?: Theme;
}

interface IProp extends IProps {
  format: string;
  placeholder?: string;
  hideValidationControl?: boolean;
}

interface IState {
  format: any;
  showCalendar: boolean;
  value: any;
  isBusinessDate: boolean;
  showClose: boolean;
}

const unit: any = 1;
const tooltipData = `d - display current date. ${moment()
  .toDate()
  .toLocaleDateString()} \nd1 - add 1 day to the current date. ${moment()
  .add(unit, 'd')
  .toDate()
  .toLocaleDateString()}\nm1 - add 1 month to the current month. ${moment()
  .add(unit, 'M')
  .toDate()
  .toLocaleDateString()}\ny1 - add 1 year to the current year. ${moment()
  .add(unit, 'Y')
  .toDate()
  .toLocaleDateString()}`;
const DevXCalendar: React.FC<IProp> = (props) => {
  let dateValue: any = '';
  if (props.value !== undefined && props.value !== null) {
    dateValue = validateDate(props.value) ? props.value : '';
  }

  const [format] = useState<IState['format']>(props.format.toUpperCase());
  const [value, setValue] = useState<IState['value']>(dateValue);
  const [isBusinessDate, setIsBusinessDate] = useState<IState['isBusinessDate']>(true);
  const [showClose, setShowClose] = useState<IState['showClose']>(false);

  if (props.theme === Theme.Light) {
    require('./LightDevXCalendar.css');
  } else {
    require('./DarkDevXCalendar.css');
  }

  const getFormattedDate = (valueParam: any) => {
    if (moment.utc(valueParam, format).isValid()) {
      const finalDate = parseDate(valueParam);
      return finalDate;
    } else {
      return undefined;
    }
  };

  const getShortcuts = async (valueParam: string) => {
    if (valueParam) {
      valueParam = valueParam.toLowerCase();
      let firstChar = valueParam.charAt(0);
      let finalValue: any = valueParam;
      let splitString: any = [];
      switch (firstChar) {
        case 'd':
          splitString = valueParam.split('d');
          if (splitString.length === 1) {
            finalValue = moment().toDate().toLocaleDateString();
          }
          break;
        case 'm':
          splitString = valueParam.split('m');
          firstChar = firstChar.toUpperCase();
          break;
        case 'y':
          splitString = valueParam.split('y');
          firstChar = firstChar.toUpperCase();
          break;
      }
      if (splitString.length === 2) {
        const val = splitString[1];
        if (!isNaN(+val)) {
          finalValue = moment().add(val, firstChar).toDate().toLocaleDateString();
        }
      }
      if (moment(finalValue, format).isValid()) {
        finalValue = moment(finalValue, format).toDate().toLocaleDateString();
      }
      return finalValue;
    }
  };

  const checkBusinessDay = async (
    actualDate: string | undefined,
    jurisdictionCodesString: string | undefined,
    nbdAdjustment: string | undefined,
  ) => {
    if (validateDate(actualDate) && props.jurisdictionCodesString) {
      actualDate = parseDate(actualDate);
      const parameters = {
        actualDate,
        jurisdictionCodesString,
        nbdAdjustment,
      };
      const result = await fetchData('Facility', 'DateBusinessDateCheck', MethodType.Post, parameters);
      if (!result.hasError && !result.hasConfirmationMessage && result.dataObject.data.length > 0) {
        const isBusinessDateValue: boolean = result.dataObject.data[0][dbConstIsBusinessDate];
        setIsBusinessDate(isBusinessDateValue);
      }
    } else {
      setIsBusinessDate(true);
    }
  };

  const getFormattedDateValue = async (date: any) => {
    let val;
    if (date && moment.utc(date).isValid()) {
      val = parseDate(date);
    }
    if (val) {
      const newValue = localeDateString(val);
      if(newValue !== value){
        setValue(newValue);
      }
    } else if (!date) {
      setValue(undefined);
    }
  };

  const onFocusIn = () => setShowClose(true);
  const onFocusOut = () => setShowClose(false);

  const onInputChange = async (e: any) => {
    const valueParam = e.event.target.value;
    const data = await getShortcuts(valueParam);
    const dts = getFormattedDate(data);
    if (props.checkBusinessDay === true) {
      await checkBusinessDay(dts, props.jurisdictionCodesString, props.nbdAdjustment);
    }
    if (dts) {
      // Added extra setState as it was causing issues with shortcuts to render the Datebox for the same props 'value'
      await Promise.resolve(setValue(undefined));
      await Promise.resolve(setValue(data));
    }
    props.onChange(dts, props.name);
    if (props.onBlur) {
      props.onBlur(dts, props.name);
    }
  };

  const onOpened = (e: any) => {
    if (props.theme !== Theme.Light) {
      e.component._popup.content().parentElement.classList.add('DateBox');
    }
  };

  const onValueSelected = async (e: any) => {
    const dt = e.value;
    if (dt) {
      const dateStr = new Date(dt).toLocaleDateString();
      await Promise.resolve(setValue(dateStr));
      const dts = getFormattedDate(dateStr);
      if (props.checkBusinessDay === true) {
        await checkBusinessDay(dts, props.jurisdictionCodesString, props.nbdAdjustment);
      }
      props.onChange(dts, props.name);
      if (props.onBlur) {
        props.onBlur(dts, props.name);
      }
    } else {
      setIsBusinessDate(true);
      setValue(undefined);
      props.onChange(undefined, props.name);
      if (props.onBlur) {
        props.onBlur('', props.name);
      }
    }
  };

  useEffect(() => {
    const loadData = async () => {
      await getFormattedDateValue(props.value);
      if (props.value && props.value !== dateValue && props.onBlur) {
        const formattedDate = getFormattedDate(props.value);
        props.onBlur(formattedDate || '', props.name);
      }
    };
    loadData();
  }, []);

  useEffect(() => {
    const loadData = async () => {
      await getFormattedDateValue(props.value);
    };
    loadData();
  }, [props.value]);

  const uid = uniqid();
  return (
    <table>
      <tbody>
        <tr>
          <td>
            <div>
              <table>
                <tbody>
                  <tr>
                    {props.hideValidationControl !== true && (
                      <td>
                        <ValidationControl
                          {...props}
                          isValid={props.isValid !== undefined ? props.isValid : true}
                          message={
                            props.validationMessage ? props.validationMessage : 'Valid date is required'
                          }
                          uid={uid}
                        />
                      </td>
                    )}
                    <td>
                      <div
                        className={props.theme === Theme.Light ? '' : 'DateBox'}
                        title={tooltipData}
                        data-testid={props.name}
                      >
                        <DateBox
                          data-testid={props.name}
                          isValid={true}
                          style={
                            props.style
                              ? props.style
                              : {
                                  // minWidth: '120px',
                                  marginLeft: '-1px',
                                  paddingRight: '0px',
                                  width: '140px',
                                }
                          }
                          onFocusIn={onFocusIn}
                          onFocusOut={onFocusOut}
                          onChange={onInputChange}
                          onOpened={onOpened}
                          stylingMode={'filled'}
                          value={value}
                          disabled={props.isDisabled}
                          placeholder={props.placeholder}
                          showClearButton={showClose}
                          pickerType={'calendar'}
                          min={new Date(1900, 0, 1)}
                          onValueChanged={onValueSelected}
                        />
                      </div>
                    </td>
                    <td>
                      {!isBusinessDate && (
                        <div
                          style={{
                            backgroundColor: 'var(--accent-grey)',
                            color: 'var(--text)',
                            padding: '2px',
                            height: '22px',
                            verticalAlign: 'middle',
                          }}
                        >
                          Non-business Day.
                        </div>
                      )}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </td>
        </tr>
      </tbody>
    </table>
  );
};

const mapStateToProps = (stateFromStore: any) => {
  return {
    format: stateFromStore.globalRdcr.dateFormat,
    placeholder: stateFromStore.globalRdcr.placeholder,
  };
};
export default connect(mapStateToProps)(DevXCalendar);
